import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Link as MuiLink,
  Divider,
  Avatar,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { openMenu, closeMenu } from '../../redux/slices/menuSlice';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../languageSwitcher';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { handleLogout } from '../../redux/slices/authSlice';
import { UserType } from '../../enums/user-type';

import logo from '../../assets/images/logo.png';
import '../../assets/styles/header.css';

function Header() {
  const dispatch = useDispatch();
  const isOpen = useSelector((state) => state.menu.isOpen);
  const [anchorEl, setAnchorEl] = useState(null); // Lokal state ile sakla
  const navigate = useNavigate();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const user = useSelector((state) => state.user.currentUser);
  const isCoach = user?.userType === UserType.Coach;
  const isAdmin = user?.userType === UserType.Admin;

  const { t: translate } = useTranslation();

  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget); // HTML öğesini burada sakla
    dispatch(openMenu());
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    dispatch(closeMenu());
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleProfileMenuOpen = (event) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const onLogout = () => {
    handleMobileMenuClose();
    dispatch(handleLogout());
    navigate('/');
  };

  const handleProfile = () => {
    handleProfileMenuClose();
    handleMobileMenuClose();
    navigate('/profile');
  };

  const handleCoachProfile = () => {
    handleProfileMenuClose();
    handleMobileMenuClose();
    navigate('/coach-profile');
  };

  const handleCertificates = () => {
    handleProfileMenuClose();
    handleMobileMenuClose();
    navigate('/coach-profile/certificates');
  };

  const handleAdmin = () => {
    handleProfileMenuClose();
    handleMobileMenuClose();
    navigate('/admin/dashboard');
  };

  const menuItems = [
    {
      text: translate('header.login'),
      to: '/signin',
      boxSx: {
        width: '100px',
        height: '36px',
        borderRadius: '9999px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.9) !important',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: 'rgba(245, 245, 245, 0.9) !important',
        },
      },
      sx: {
        color: 'rgba(128, 128, 128, 0.9) !important',
        textDecoration: 'none',
        fontSize: '0.875rem',
        fontWeight: 400,
        textTransform: 'capitalize',
      },
      mobileSx: {
        textDecoration: 'none',
        color: 'inherit',
      },
    },
    {
      text: translate('header.signup'),
      to: '/signup',
      boxSx: {
        width: '100px',
        height: '36px',
        borderRadius: '9999px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(128, 128, 128, 0.9) !important',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: 'rgba(100, 100, 100, 0.9) !important',
        },
      },
      sx: {
        color: 'rgba(255, 255, 255, 0.9) !important',
        textDecoration: 'none',
        fontSize: '0.875rem',
        fontWeight: 400,
        textTransform: 'capitalize',
      },
      mobileSx: {
        textDecoration: 'none',
        color: 'inherit',
      },
    },
  ];

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: 'rgba(255, 255, 255, 0.7) !important',
        backdropFilter: 'blur(10px);',
        boxShadow: '3px 3px 10px rgba(0, 0, 0, 0.1)',
        color: 'rgba(128, 128, 128, 0.9) !important',
      }}
    >
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 20px' }}>
        <RouterLink to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img src={logo} alt="Coaching Search Engine" className="logo" height={64} width={64} />
            Coaching Search Engine
          </Box>
        </RouterLink>

        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, alignItems: 'center', ml: 'auto' }}>
          {isAuthenticated ? (
            <>
              <Box
                onClick={handleProfileMenuOpen}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                <Avatar src={user?.imageUrl} alt={user?.firstName} sx={{ width: 36, height: 36 }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(128, 128, 128, 0.9)',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    letterSpacing: '0.01em',
                  }}
                >
                  {user?.firstName} {user?.lastName}
                </Typography>
              </Box>
              <Menu
                anchorEl={profileMenuAnchor}
                open={Boolean(profileMenuAnchor)}
                onClose={handleProfileMenuClose}
                sx={{
                  mt: 1,
                  '& .MuiPaper-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '3px 3px 10px rgba(0, 0, 0, 0.1)',
                    minWidth: '200px',
                  },
                  '& .MuiMenuItem-root': {
                    fontSize: '0.875rem',
                    fontWeight: 400,
                    letterSpacing: '0.01em',
                    color: 'rgba(0, 0, 0, 0.87)',
                    minHeight: '36px',
                    padding: '6px 16px',
                  },
                }}
              >
                <MenuItem onClick={handleProfile}>{translate('header.profile')}</MenuItem>
                {isCoach || isAdmin
                  ? [
                      <MenuItem key="coach-profile" onClick={handleCoachProfile}>
                        {translate('header.coach-profile')}
                      </MenuItem>,
                      <MenuItem key="certificates" onClick={handleCertificates}>
                        {translate('header.certificates')}
                      </MenuItem>,
                    ]
                  : null}
                {isAdmin && (
                  <MenuItem key="admin" onClick={handleAdmin}>
                    {translate('header.admin')}
                  </MenuItem>
                )}
                <Divider />
                <MenuItem onClick={onLogout}>{translate('header.logout')}</MenuItem>
              </Menu>
            </>
          ) : (
            <>
              {menuItems.map((item) => (
                <Box key={item.text} sx={item.boxSx}>
                  <RouterLink
                    to={item.to}
                    style={{ textDecoration: 'none' }}
                    onClick={() => {
                      handleMobileMenuClose();
                    }}
                  >
                    <Box sx={item.sx}>{item.text}</Box>
                  </RouterLink>
                </Box>
              ))}
            </>
          )}
          <LanguageSwitcher />
        </Box>

        <IconButton
          sx={{ display: { xs: 'flex', md: 'none' } }}
          edge="end"
          color="inherit"
          onClick={handleMobileMenuOpen}
        >
          <MenuIcon />
        </IconButton>

        <Menu
          anchorEl={mobileMenuAnchor}
          open={Boolean(mobileMenuAnchor)}
          onClose={handleMobileMenuClose}
          sx={{
            '& .MuiPaper-root': {
              minWidth: '200px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              boxShadow: '3px 3px 10px rgba(0, 0, 0, 0.1)',
            },
            '& .MuiMenuItem-root': {
              fontSize: '0.875rem',
              fontWeight: 400,
              letterSpacing: '0.01em',
              color: 'rgba(0, 0, 0, 0.87)',
              minHeight: '36px',
              padding: '6px 16px',
            },
          }}
        >
          {[
            ...(isAuthenticated
              ? [
                  <MenuItem key="profile" onClick={handleProfile}>
                    {translate('header.profile')}
                  </MenuItem>,
                  ...(isCoach || isAdmin
                    ? [
                        <MenuItem key="coach-profile" onClick={handleCoachProfile}>
                          {translate('header.coach-profile')}
                        </MenuItem>,
                        <MenuItem key="certificates" onClick={handleCertificates}>
                          {translate('header.certificates')}
                        </MenuItem>,
                      ]
                    : []),
                  ...(isAdmin
                    ? [
                        <MenuItem key="admin" onClick={handleAdmin}>
                          {translate('header.admin')}
                        </MenuItem>,
                      ]
                    : []),
                  <MenuItem key="logout" onClick={onLogout}>
                    {translate('header.logout')}
                  </MenuItem>,
                  <Divider key="divider" />,
                ]
              : menuItems.map((item) => (
                  <MenuItem
                    key={item.text}
                    onClick={() => {
                      handleMobileMenuClose();
                      navigate(item.to);
                    }}
                  >
                    {item.text}
                  </MenuItem>
                ))),
            <MenuItem key="language">
              <LanguageSwitcher />
            </MenuItem>,
          ]}
        </Menu>
      </Toolbar>
    </AppBar>
  );
}

export default Header;
